import { useCallback } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { updateTrackCar } from "../graphql/mutations";
import { UNSPOTTED_TRACK_ID, ResourceType } from "./constants";
import {
  calculateMoveState,
  performMovePersistence,
  dedupeTrackState,
  getTrackItemKey,
} from "./trackMoveLogic";
import { sortTrackCars } from "./trackListUtils";
import { withTimePulledHistory } from "./trackCarPersistence";
import {
  isPrePlacementInboundStatus,
  isPrePlacementOutboundStatus,
  spotRefusalNotice,
} from "./shipmentPlacement";

/**
 * Shared hook for track car operations (add, remove, move, delete).
 *
 * Provides consistent CRUD logic for both Manager and Crew applications.
 * Handles optimistic updates, persistence, and error recovery.
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.trackData - Current track data map (trackId -> cars[])
 * @param {Function} options.setTrackData - Setter for track data state
 * @param {Function} options.setLoading - Setter for loading state
 * @param {Function} options.saveShipment - Function to persist shipment updates
 * @param {Function} [options.notify] - Optional notification function
 * @param {Function} [options.logError] - Error logging function
 * @param {Function} [options.redirect] - Navigation redirect function
 * @param {Function} [options.onSuccess] - Callback on successful operation
 * @param {Function} [options.onError] - Callback on failed operation
 * @param {string} [options.unspottedTrackId=UNSPOTTED_TRACK_ID] - ID of virtual "unspotted" track
 * @param {Function} [options.refreshTracks] - Function to refresh track capacities
 * @param {Function} [options.getShipments] - Function to refetch shipments
 *
 * @returns {Object} Hook API
 * @returns {Function} returns.handleTrackDrop - Handle drag-and-drop move operation
 * @returns {Function} returns.moveShipments - Programmatically move shipments
 * @returns {Function} returns.removeFromTrack - Remove shipment from track
 * @returns {Function} returns.deleteItem - Mark railcar as collected and deactivate TrackCar
 * @returns {Function} returns.isUnspottedTrack - Check if track ID is virtual unspotted
 *
 * @example
 * const { handleTrackDrop, moveShipments } = useTrackOperations({
 *   trackData: processedCarsMap,
 *   setTrackData: setProcessedCarsMap,
 *   setLoading,
 *   saveShipment,
 *   notify,
 *   logError,
 *   unspottedTrackId: UNSPOTTED_TRACK_ID,
 *   refreshTracks: refreshTracksDebounced,
 * });
 */
export const useTrackOperations = ({
  trackData,
  setTrackData,
  setLoading,
  saveShipment,
  notify,
  logError,
  redirect,
  onSuccess,
  onError,
  unspottedTrackId = UNSPOTTED_TRACK_ID,
  refreshTracks,
  getShipments,
  tracks,
  resolveParentType,
  // Optional callback for updating shipment status/timestamps when first placed (unspotted -> track)
  updateShipmentOnPlacement,
}) => {
  const canonicalUnspottedTrackId = unspottedTrackId || UNSPOTTED_TRACK_ID;

  const isUnspottedTrack = useCallback(
    (trackId) => trackId === canonicalUnspottedTrackId,
    [canonicalUnspottedTrackId],
  );

  /**
   * Handles a drag-and-drop or programmatic move operation.
   */
  const handleTrackDrop = useCallback(
    async (
      event,
      targetTrackId,
      dropTarget,
      trackDataOverride,
      finalVisualState,
    ) => {
      if (event && typeof event.preventDefault === "function")
        event.preventDefault();

      let shipmentIds = [];
      if (event && event.dataTransfer) {
        const dataString = event.dataTransfer.getData("application/json");
        if (dataString) {
          const transferPayload = JSON.parse(dataString);
          shipmentIds = transferPayload.shipmentIds || [];
        }
      }

      if (!shipmentIds.length) return;

      if (setLoading) setLoading(true);

      const baseTrackData = trackDataOverride || trackData;

      // ELITE STAFF LEVEL FIX: Deep-clone pre-move state to make rollback reliable.
      // originalTrackData must be a true snapshot because performMovePersistence
      // can mutate item objects in-place during sort-key resequencing.
      const originalTrackData = Object.fromEntries(
        Object.entries(baseTrackData || {}).map(([id, list]) => [
          id,
          (list || []).map((item) => ({ ...item })),
        ]),
      );

      const moveState = calculateMoveState({
        shipmentIds,
        targetTrackId,
        dropTarget,
        baseTrackData,
        trackDataOverride,
        finalVisualState,
        isUnspottedTrack,
        tracks,
      });

      if (!moveState) {
        if (setLoading) setLoading(false);
        return;
      }

      const {
        workingState,
        itemsToInsert,
        affectedTrackIds,
        originalTrackIds,
      } = moveState;

      const dedupedState = dedupeTrackState(workingState);
      setTrackData(dedupedState);

      try {
        const affectedIds = await performMovePersistence({
          workingState,
          affectedTrackIds,
          setLoading,
          setTrackData,
          notify,
          onSuccess,
          originalTrackData,
          targetTrackId,
          isUnspottedTrack,
          resolveParentType,
          tracks,
        });

        // Landing a car on a real track places its shipment: inbound -> PLACED,
        // outbound -> ARRIVED, plus related timestamps. Usually that is the
        // first move out of Unspotted, but a shipment still waiting to be
        // placed gets another attempt on every later move.
        if (
          affectedIds &&
          updateShipmentOnPlacement &&
          !isUnspottedTrack(targetTrackId)
        ) {
          const getShipmentParentType = (shipment) => {
            const parentType = resolveParentType
              ? resolveParentType(shipment)
              : shipment?.parentType;
            if (
              parentType !== ResourceType.INBOUND &&
              parentType !== ResourceType.OUTBOUND
            ) {
              return null;
            }
            return parentType;
          };
          // Each direction is asked only about its own status set. The two
          // enums overlap - OutboundStatus.ARRIVED is the same string as
          // InboundStatus.ARRIVED - so a car whose direction is unknown cannot
          // be given a default branch: reading an outbound's status against the
          // inbound set is what a fallback would silently do.
          const needsPlacementForKind = (shipmentKind, shipmentStatus) => {
            if (shipmentKind === ResourceType.INBOUND) {
              return isPrePlacementInboundStatus(shipmentStatus);
            }
            if (shipmentKind === ResourceType.OUTBOUND) {
              return isPrePlacementOutboundStatus(shipmentStatus);
            }
            return false;
          };

          const invalidShipments = [];
          // Cars that were spotted but whose shipment could not be placed. One
          // message for the whole drop: react-admin queues notifications and
          // plays them one after another, so a notify per car would drip past
          // the manager as a stream of snackbars instead of one readable count.
          const refusedPlacements = [];
          for (const shipmentToInsert of itemsToInsert) {
            // The same key calculateMoveState booked the car under — a TrackCar
            // row keys by its shipmentId, and a bare-id lookup would miss it,
            // leaving its source track unknown.
            const shipmentKey = getTrackItemKey(shipmentToInsert);
            const sourceTrackId = originalTrackIds.get(shipmentKey);

            // TrackCar: status/state live in inbound ?? outbound
            const connectedShipment =
              shipmentToInsert?.inbound ?? shipmentToInsert?.outbound;
            const shipmentStatus =
              shipmentToInsert?.status ?? connectedShipment?.status ?? null;
            const shipmentState =
              shipmentToInsert?.state ?? connectedShipment?.state ?? null;

            const shipmentKind = getShipmentParentType(shipmentToInsert);
            const needsPlacement = needsPlacementForKind(
              shipmentKind,
              shipmentStatus,
            );

            // The first drop out of Unspotted is the usual placement, but not
            // the only one: placement is refused while an inbound has no
            // inventory, and the inventory is normally entered after the car is
            // already sitting on a track. Any later move re-attempts it, so
            // long as the shipment is still waiting to be placed. A car that is
            // already PLACED or beyond is only being shuffled, and skipping it
            // here also keeps the invalid-shipment logging below scoped to
            // shipments that were actually candidates. A car that came out of
            // Unspotted is examined whatever its status, so a direction that
            // could not be resolved is still reported below rather than
            // dropped.
            if (
              sourceTrackId !== canonicalUnspottedTrackId &&
              !needsPlacement
            ) {
              continue;
            }

            const missingFields = [];
            if (!shipmentKind) missingFields.push("parentType");
            if (shipmentStatus == null) missingFields.push("status");
            if (
              shipmentKind === ResourceType.INBOUND &&
              shipmentState == null
            ) {
              missingFields.push("state");
            }

            if (missingFields.length > 0) {
              invalidShipments.push({
                id: shipmentToInsert?.id || "unknown",
                status: shipmentStatus ?? "missing",
                state: shipmentState ?? "missing",
                parentType: shipmentKind ?? "missing",
                missingFields: missingFields.join("|"),
              });
              continue;
            }
            if (needsPlacement) {
              try {
                const refusal = await updateShipmentOnPlacement(
                  {
                    ...shipmentToInsert,
                    trackId: targetTrackId,
                    status: shipmentStatus,
                    state: shipmentState,
                  },
                  shipmentStatus,
                  targetTrackId,
                );
                if (refusal) refusedPlacements.push(refusal);
              } catch (err) {
                logError(
                  "useTrackOperations:handleTrackDrop:updateShipmentOnPlacement",
                  err,
                  notify,
                );
              }
            }
          }
          if (refusedPlacements.length > 0 && notify) {
            notify(spotRefusalNotice(refusedPlacements), "warning");
          }
          if (invalidShipments.length > 0) {
            const sample = invalidShipments
              .slice(0, 5)
              .map(
                (entry) =>
                  `${entry.id}:${entry.parentType}:${entry.status}:${entry.state}:${entry.missingFields}`,
              )
              .join(", ");
            logError(
              "useTrackOperations:handleTrackDrop:unableToInferShipmentType",
              new Error(
                `Missing canonical shipment fields for ${invalidShipments.length} moved shipment(s). Sample: ${sample}`,
              ),
              notify,
            );
          }
        }

        return affectedIds;
      } catch (err) {
        notify("Move failed. Changes reverted.", "error");
        logError("useTrackOperations:handleTrackDrop", err, false);
        if (onError) onError(err);
      }
    },
    [
      trackData,
      setTrackData,
      setLoading,
      notify,
      logError,
      onSuccess,
      onError,
      isUnspottedTrack,
      tracks,
      resolveParentType,
      updateShipmentOnPlacement,
      canonicalUnspottedTrackId,
    ],
  );

  /**
   * Programmatic add item to track.
   */
  const addItem = useCallback(
    async ({
      shipment,
      targetTrack,
      index = -1,
      updateShipmentOnPlacement,
    }) => {
      if (!shipment?.id || !targetTrack) return;

      const targetTrackId = targetTrack.locations?.items?.[0]?.id;
      if (!targetTrackId || isUnspottedTrack(targetTrackId)) return;

      const syntheticEvent = {
        dataTransfer: {
          getData: () => JSON.stringify({ shipmentIds: [shipment.id] }),
        },
      };

      const currentState = { ...trackData };
      const unspottedId = canonicalUnspottedTrackId;

      if (!currentState[unspottedId]) currentState[unspottedId] = [];

      // Ensure clean start: remove from everywhere
      Object.keys(currentState).forEach((tid) => {
        currentState[tid] = (currentState[tid] || []).filter(
          (c) => c.id !== shipment.id,
        );
      });

      // Add to virtual unspotted for the move logic to pick it up as a "create" operation
      currentState[unspottedId].push({
        ...shipment,
        trackId: unspottedId,
      });

      const insertIndex =
        index < 0 ? currentState[targetTrackId]?.length || 0 : index;

      const affectedIds = await handleTrackDrop(
        syntheticEvent,
        targetTrackId,
        { index: insertIndex },
        currentState,
        null,
      );

      if (affectedIds && updateShipmentOnPlacement) {
        const connectedShipment = shipment.inbound ?? shipment.outbound;
        const status = shipment.status ?? connectedShipment?.status ?? null;
        try {
          const refusal = await updateShipmentOnPlacement(
            { ...shipment, trackId: targetTrackId },
            status,
          );
          // Spotted, but the shipment stayed put — say so, the same way the
          // drag-and-drop path reports its refusals.
          if (refusal && notify)
            notify(spotRefusalNotice([refusal]), "warning");
        } catch (err) {
          logError(
            "useTrackOperations:addItem:updateShipmentOnPlacement",
            err,
            notify,
          );
        }
      }

      if (affectedIds && refreshTracks) refreshTracks(affectedIds);
      if (affectedIds && getShipments) {
        const visibleTrackIds = Array.isArray(affectedIds)
          ? affectedIds
          : [targetTrackId].filter(Boolean);
        getShipments({ showSpinner: true, visibleTrackIds });
      }
    },
    [
      handleTrackDrop,
      trackData,
      isUnspottedTrack,
      canonicalUnspottedTrackId,
      refreshTracks,
      getShipments,
      logError,
      notify,
    ],
  );

  /**
   * Programmatic remove item from track (move to Unspotted).
   */
  const removeItem = useCallback(
    async ({
      shipmentId,
      trackLocationId,
      updateShipmentOnPlacement,
      trackDataOverride = null,
    }) => {
      if (!shipmentId || !trackLocationId) return;

      const syntheticEvent = {
        dataTransfer: {
          getData: () => JSON.stringify({ shipmentIds: [shipmentId] }),
        },
      };

      const baseTrackData = trackDataOverride || trackData;
      const currentState = { ...(baseTrackData || {}) };
      const targetTrackId = canonicalUnspottedTrackId;

      const affectedIds = await handleTrackDrop(
        syntheticEvent,
        targetTrackId,
        { index: 0 },
        currentState,
        null,
      );

      if (affectedIds && updateShipmentOnPlacement) {
        const car = (baseTrackData?.[trackLocationId] || []).find(
          (c) => c.id === shipmentId,
        );
        if (car) {
          const connectedShipment = car.inbound ?? car.outbound;
          const status = car.status ?? connectedShipment?.status ?? null;
          try {
            await updateShipmentOnPlacement(
              { ...car, trackId: canonicalUnspottedTrackId },
              status,
            );
          } catch (err) {
            logError(
              "useTrackOperations:removeItem:updateShipmentOnPlacement",
              err,
              notify,
            );
          }
        }
      }

      // Only refresh capacity for real tracks (exclude unspotted). Do not call getShipments:
      // state was already updated by handleTrackDrop; refetching would trigger getTracks +
      // getTrackCars (Area, Aisle, GetLocation, TrackCar queries) and duplicate drag-to-unspot behavior.
      if (affectedIds && refreshTracks) {
        const realTrackIds = Array.isArray(affectedIds)
          ? affectedIds.filter((id) => id && id !== canonicalUnspottedTrackId)
          : [trackLocationId].filter(Boolean);
        if (realTrackIds.length > 0) refreshTracks(realTrackIds);
      }
    },
    [
      handleTrackDrop,
      trackData,
      canonicalUnspottedTrackId,
      refreshTracks,
      logError,
      notify,
    ],
  );

  /**
   * Programmatic delete item (mark as collected).
   */
  const deleteItem = useCallback(
    async ({ shipmentId, trackLocationId }) => {
      if (!shipmentId || !trackLocationId) return;

      setLoading(true);
      try {
        const currentState = { ...trackData };
        const trackList = currentState[trackLocationId] || [];
        const shipmentIndex = trackList.findIndex((c) => c.id === shipmentId);

        if (shipmentIndex === -1) {
          setLoading(false);
          return;
        }

        const trackCar = trackList[shipmentIndex];

        // 1. Update Shipment record (mark collected)
        // Use a minimal payload so we do not accidentally update composite-key fields
        // with partial data from lean TrackCar rows.
        const shipmentUpdate = {
          id: trackCar.id,
          parentType: trackCar.parentType,
          isCollected: true,
          collectedDateTime: new Date().toISOString(),
        };
        await saveShipment(await withTimePulledHistory(shipmentUpdate));

        // 2. Mark TrackCar record as inactive after shipment update
        await API.graphql(
          graphqlOperation(updateTrackCar, {
            input: {
              id: trackCar.id,
              active: "false",
              trackId: "",
              sortKey: "",
              eodTrackId: trackCar.eodTrackId ?? trackCar.trackId ?? "",
              eodSortKey: trackCar.eodSortKey ?? trackCar.sortKey ?? "",
            },
          }),
        );

        // 3. Update local state
        const nextList = trackList.filter((c) => c.id !== shipmentId);
        const nextState = {
          ...trackData,
          [trackLocationId]: sortTrackCars(nextList),
        };
        setTrackData(nextState);

        if (notify) notify("Railcar collected and removed from track", "info");
        if (onSuccess) onSuccess();
        if (refreshTracks) refreshTracks([trackLocationId]);
        if (getShipments) {
          getShipments({
            showSpinner: true,
            visibleTrackIds: [trackLocationId],
          });
        }
      } catch (err) {
        logError("useTrackOperations:deleteItem", err, notify);
        if (onError) onError(err);
      } finally {
        setLoading(false);
      }
    },
    [
      trackData,
      setTrackData,
      setLoading,
      saveShipment,
      logError,
      notify,
      onSuccess,
      onError,
      refreshTracks,
      getShipments,
    ],
  );

  return { handleTrackDrop, addItem, removeItem, deleteItem };
};
